import { updateCachedAuthor } from '../services/homeFeedSession';
import { apiRequest, buildQuery } from './client';
import { followWithCooldown } from '../services/followCooldown';

export const backendProfileChangedEventName = 'amposter:backend-profile-changed';

export const toLegacyProfile = (profile) => {
  if (!profile) return null;
  const primaryRole = Array.isArray(profile.roles) ? profile.roles[0] : [...(profile.roles || [])][0];
  return {
    ...profile,
    uid: profile.id,
    name: profile.displayName || profile.displayLabel,
    fullName: profile.displayName || profile.displayLabel,
    username: profile.userHandle,
    tag: profile.userHandle,
    img: profile.avatarUrl,
    frame_url: profile.frameUrl,
    frame_border_radius: profile.frameBorderRadius, frame_fit: profile.frameFit,
    cover_img: profile.coverUrl,
    description: profile.bio,
    completed_profile: profile.profileCompleted,
    sensitive_img: profile.sensitiveAvatar,
    sensitive_cover: profile.sensitiveCover,
    verified: profile.verified,
    status: profile.presenceStatus,
    role: primaryRole || null,
    followersCount: profile.followerCount || 0,
    followingCount: profile.followingCount || 0,
  };
};

export const profilesApi = {
  handleAvailability: (handle, signal) => apiRequest(`/api/v1/profiles/me/handle-availability${buildQuery({ handle })}`, { signal, cache: false }),
  me: async (options) => toLegacyProfile(await apiRequest('/api/v1/profiles/me', options)),
  get: async (userId) => toLegacyProfile(await apiRequest(`/api/v1/profiles/${encodeURIComponent(userId)}`)),
  preview: async (identifier) => {
    const result = await apiRequest(`/api/v1/profiles/${encodeURIComponent(identifier)}/preview`);
    return { ...toLegacyProfile(result.profile), badges: result.badges || [] };
  },
  update: async (changes) => {
    const profile = toLegacyProfile(await apiRequest('/api/v1/profiles/me', { method: 'PATCH', body: changes }));
    window.dispatchEvent(new CustomEvent(backendProfileChangedEventName, { detail: profile }));
    return profile;
  },
  follow: followWithCooldown,
  unfollow: (userId) => apiRequest(`/api/v1/profiles/${userId}/follow`, { method: 'DELETE' }),
  enablePostNotifications: (userId) => apiRequest(`/api/v1/profiles/${userId}/post-notifications`, { method: 'PUT' }),
  disablePostNotifications: (userId) => apiRequest(`/api/v1/profiles/${userId}/post-notifications`, { method: 'DELETE' }),
  block: (userId) => apiRequest(`/api/v1/profiles/${userId}/block`, { method: 'POST' }),
  unblock: (userId) => apiRequest(`/api/v1/profiles/${userId}/block`, { method: 'DELETE' }),
  blockedUsers: (search = '') => apiRequest(`/api/v1/profiles/me/blocked-users${buildQuery({ search })}`),
  moderateMedia: async (userId, body) => toLegacyProfile(await apiRequest(`/api/v1/admin/profiles/${userId}/media`, { method: 'PATCH', body })),
  uploadMedia: (kind, file) => {
    const body = new FormData();
    body.append('file', file);
    return apiRequest(`/api/v1/profiles/me/media/${kind}`, { method: 'POST', body, timeoutMs: 120000 });
  },
  setVerified: async (userId, verified) => {
    const result = await apiRequest(`/api/v1/admin/profiles/${userId}/verification`, { method: 'PUT', body: { verified } });
    updateCachedAuthor(userId, { verified: result.verified });
    window.dispatchEvent(new CustomEvent('amposter:verification-changed', { detail: result }));
    return result;
  },
  titles: (userId) => apiRequest(`/api/v1/profiles/${userId}/titles`),
  badges: (userId) => apiRequest(`/api/v1/profiles/${userId}/badges`),
  reorderBadges: (badgeIds) => apiRequest('/api/v1/me/profile/badges/order', { method: 'PUT', body: { badgeIds } }),
  reorderTitles: (userId, titleIds) => apiRequest(`/api/v1/admin/profiles/${userId}/titles/order`, { method: 'PUT', body: { titleIds } }),
  grantTitle: (userId, body) => apiRequest(`/api/v1/admin/profiles/${userId}/titles`, { method: 'POST', body }),
  removeTitle: (userId, titleId) => apiRequest(`/api/v1/admin/profiles/${userId}/titles/${titleId}`, { method: 'DELETE' }),
  grantBadge: (userId, body) => apiRequest(`/api/v1/admin/profiles/${userId}/badges`, { method: 'POST', body }),
  removeBadge: (userId, badgeId) => apiRequest(`/api/v1/admin/profiles/${userId}/badges/${badgeId}`, { method: 'DELETE' }),
  badgeCatalog: () => apiRequest('/api/v1/admin/profile-badges'),
  frame: (userId) => apiRequest(`/api/v1/profiles/${userId}/frame`),
  followers: (userId, options) => apiRequest(`/api/v1/profiles/${userId}/followers${buildQuery(options)}`),
  following: (userId, options) => apiRequest(`/api/v1/profiles/${userId}/following${buildQuery(options)}`),
};
